import { Check, ChevronRight, Trophy } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { EmptyState, ErrorState, LoadingState, PreviewBoundary } from '../components/StatePanels'
import { Meter } from '../components/Ui'
import { PrimaryShell, SecondaryShell, usePreviewState } from '../components/Shell'
import {
  badgeAsset,
  badgeAssets,
  characterAssets,
  cultureBadgeAssets,
  gardenStages,
  gratitudeAssets,
  homeAssets,
} from '../data/assets'
import type { ThanksActivity, TrainingAreaStatus, TrainingModule } from '../data/app-adapter'
import { adapterLoaders, useAdapterData } from '../data/use-adapter-data'
import { useSession } from '../session/SessionProvider'

function AsyncGuard<T>({ state, children }: { state: ReturnType<typeof useAdapterData<T>>; children(data: T): React.ReactNode }) {
  if (state.status === 'loading') return <LoadingState />
  if (state.status === 'error') return <ErrorState onRetry={state.retry} />
  return children(state.data)
}

const moduleBadgePaths: Record<string, string> = {
  front: '勳章/個人站台A級/外場.png',
  packing: '勳章/個人站台A級/包餐.png',
  prep: '勳章/個人站台A級/備料.png',
  counter: '勳章/個人站台A級/櫃台.png',
  soup: '勳章/個人站台A級/湯區.png',
  egg: '勳章/個人站台A級/煎蛋.png',
  vegetable: '勳章/個人站台A級/菜區.png',
  rice: '勳章/個人站台A級/飯區.png',
  weighing: '勳章/個人站台A級/秤料.png',
  'steamed-egg': '勳章/個人站台A級/蒸盤.png',
}

const trainingAreas = [
  { key: 'academic', label: '學科', mark: '◆' },
  { key: 'practical', label: '術科', mark: '⚔' },
  { key: 'teaching', label: '帶教', mark: '♟' },
] as const

const emptyResultCopy = {
  academic: '尚未進行學科考試',
  practical: '尚未進行術科考核',
  teaching: '尚未進行帶教訓練',
} as const

function areaTone(area: TrainingAreaStatus) {
  return `training-state training-state--${area.state}`
}

export function HomePage() {
  const { user } = useSession()
  const data = useAdapterData(adapterLoaders.home)
  const previewState = usePreviewState()
  return (
    <PrimaryShell>
      <PreviewBoundary state={previewState}>
        <AsyncGuard state={data}>{(home) => (
          <div className="home-page">
            <section className="home-hero" style={{ backgroundImage: `url(${homeAssets.background})` }}>
              <div className="home-identity">
                <img src={characterAssets.male} alt={`${user?.name ?? '員工'}的 Q 版人物`} />
                <div><h1>{user?.name}</h1><p>{user?.roleLabel}</p><strong>{user?.storeName}</strong></div>
              </div>
            </section>

            <Link to={`/broadcasts/${home.announcement.id}`} className="broadcast-card">
              <span className="broadcast-card__icon">◈</span>
              <div><small>系統廣播</small><h2>{home.announcement.title}</h2><p>{home.announcement.summary}</p></div>
              <ChevronRight aria-hidden="true" />
            </Link>

            <section className="paper-card daily-tasks">
              <header><h2>今日任務</h2><span>{home.tasks.filter((task) => task.completed).length}/{home.tasks.length}</span></header>
              {home.tasks.map((task) => (
                <div className="task-row" key={task.id}>
                  <span className={task.completed ? 'check is-done' : 'check'}>{task.completed && <Check />}</span>
                  <span>{task.label}</span>
                  <small>{task.completed ? '已完成' : '待完成'}</small>
                </div>
              ))}
            </section>

            <section className="daily-letter"><small>每日家書</small><blockquote>{home.dailyLetter}</blockquote><span>VASO HOME</span></section>
          </div>
        )}</AsyncGuard>
      </PreviewBoundary>
    </PrimaryShell>
  )
}

export function GrowthPage() {
  const data = useAdapterData(adapterLoaders.training)
  const previewState = usePreviewState()
  return (
    <PrimaryShell title="成長任務">
      <PreviewBoundary state={previewState}>
        <AsyncGuard state={data}>{(modules) => {
          if (modules.length === 0) return <EmptyState />
          const completed = modules.filter((module) => module.status === 'completed').length
          const averageProgress = Math.round(modules.reduce((sum, module) => sum + module.progress, 0) / modules.length)
          return <div className="growth-page">
            <section className="progress-overview paper-card">
              <img src={badgeAsset('勳章/教育勳章/個人教育模板.png')} alt="成長任務教育徽章" />
              <div><small>新人訓練進度</small><strong>{averageProgress}%</strong><p>已完成 {completed}/{modules.length} 個訓練模組</p></div>
              <Meter value={averageProgress} />
            </section>
            <section className="module-list" aria-label="訓練模組">
              {modules.map((module) => (
                <article key={module.id} className="module-card paper-card">
                  <img src={badgeAsset(moduleBadgePaths[module.id] ?? moduleBadgePaths.front)} alt={`${module.title}訓練徽章`} loading="lazy" />
                  <div><header><h2>{module.title}</h2><strong>{module.progress}%</strong></header><p>{module.description}</p>
                    <div className="module-statuses" role="group" aria-label={`${module.title}三階段完成度`}>{trainingAreas.map((area) => <span key={area.key}><b>{area.label}</b><strong>{module.areas[area.key].progress}%</strong></span>)}</div>
                    <nav className="module-actions" aria-label={`${module.title}學習入口`}>
                      <Link to={`/training/${module.id}/exam`}>學科</Link>
                      <Link to={`/training/${module.id}/results#practical`}>術科</Link>
                      <Link to={`/training/${module.id}/teaching`}>帶教</Link>
                    </nav>
                  </div>
                </article>
              ))}
            </section>
          </div>
        }}</AsyncGuard>
      </PreviewBoundary>
    </PrimaryShell>
  )
}

export function TrainingDetailPage() {
  const { moduleId = 'front' } = useParams()
  const data = useAdapterData(adapterLoaders.training)
  return (
    <SecondaryShell title="訓練詳情" backTo="/growth">
      <AsyncGuard state={data}>{(modules) => {
        const module = modules.find((item) => item.id === moduleId)
        if (!module) return <EmptyState />
        return <div className="training-detail-page">
          <section className="training-summary paper-card">
            <div className="training-summary__heading">
              <img src={badgeAsset(moduleBadgePaths[module.id] ?? moduleBadgePaths.front)} alt={`${module.title}訓練徽章`} />
              <div><h2>{module.title}訓練</h2><p>{module.description}</p></div>
            </div>
            <div className="training-progress"><div><small>整體進度</small><strong>{module.progress}%</strong></div><div><Meter value={module.progress} /><p>Preview 合成狀態，未連正式訓練記錄</p></div></div>
          </section>
          <section className="training-entry-list" aria-label="訓練入口">
            <TrainingEntry module={module} area="academic" to={`/training/${module.id}/exam`} />
            <TrainingEntry module={module} area="practical" to={`/training/${module.id}/results#practical`} />
            <TrainingEntry module={module} area="teaching" to={`/training/${module.id}/teaching`} />
          </section>
          <Link className="primary-button" to={`/training/${module.id}/results`}>查看成果</Link>
        </div>
      }}</AsyncGuard>
    </SecondaryShell>
  )
}

function TrainingEntry({ module, area, to }: { module: TrainingModule; area: keyof TrainingModule['areas']; to: string }) {
  const meta = trainingAreas.find((item) => item.key === area)!
  const status = module.areas[area]
  return <Link className="training-entry paper-card" to={to}><span aria-hidden="true">{meta.mark}</span><div><h3>{meta.label}</h3><p className={areaTone(status)}>{status.label}</p></div><ChevronRight aria-hidden="true" /></Link>
}

function useTrainingModule() {
  const { moduleId = 'front' } = useParams()
  const data = useAdapterData(adapterLoaders.training)
  return { moduleId, data }
}

function TrainingRouteGuard({ title, children }: { title: string; children(module: TrainingModule): React.ReactNode }) {
  const { moduleId, data } = useTrainingModule()
  return <SecondaryShell title={title} backTo={`/training/${moduleId}`}><AsyncGuard state={data}>{(modules) => {
    const module = modules.find((item) => item.id === moduleId)
    return module ? children(module) : <EmptyState />
  }}</AsyncGuard></SecondaryShell>
}

export function TrainingExamPage() {
  return <TrainingRouteGuard title="學科">{(module) => <TrainingShellContent module={module} area="academic" heading="學科考試" description="此頁僅顯示可追蹤狀態。正式考試送出與批改尚未接線。" />}</TrainingRouteGuard>
}

export function TrainingTeachingPage() {
  return <TrainingRouteGuard title="帶教">{(module) => <TrainingShellContent module={module} area="teaching" heading="帶教 Checklist" description="此頁僅顯示學員狀態。送主管確認、退回與再次送出尚未接線。" />}</TrainingRouteGuard>
}

function TrainingShellContent({ module, area, heading, description }: { module: TrainingModule; area: keyof TrainingModule['areas']; heading: string; description: string }) {
  return <div className="training-support-page"><section className="paper-card"><small>{module.title}訓練</small><h2>{heading}</h2><p className={areaTone(module.areas[area])}>{module.areas[area].label}</p><p id="training-disabled-reason">{description}</p></section><button type="button" className="primary-button" disabled aria-describedby="training-disabled-reason">未連正式系統</button></div>
}

export function TrainingResultsPage() {
  return <TrainingRouteGuard title="訓練成果">{(module) => <div className="training-results-page"><header><small>{module.title}訓練</small><h2>我的成果</h2><p>Preview 只讀狀態，未連正式成果資料。</p></header>{trainingAreas.map((area) => <section id={area.key === 'practical' ? 'practical' : undefined} className="result-block paper-card" key={area.key}><span>{area.mark}</span><div><h3>{area.label}成果</h3><p className="training-state training-state--unknown">{emptyResultCopy[area.key]}</p></div></section>)}</div>}</TrainingRouteGuard>
}

export function GardenPage() {
  const data = useAdapterData(adapterLoaders.revenue)
  const previewState = usePreviewState()
  return (
    <PrimaryShell title="家園">
      <PreviewBoundary state={previewState}>
        <AsyncGuard state={data}>{(revenue) => {
          const rate = Math.round(revenue.annualRevenue / revenue.annualTarget * 1000) / 10
          const stage = gardenStages.find((item) => rate <= item.max) ?? gardenStages.at(-1)!
          return <div className="garden-page">
            <section className="garden-hero"><img src={stage.src} alt={`${stage.label}家園場景`} /><span>{stage.label}</span></section>
            <section className="revenue-card paper-card"><div><small>年度營業額</small><h2>{revenue.annualRevenue.toLocaleString()} <span>/ {revenue.annualTarget.toLocaleString()}</span></h2><Meter value={rate} /><p>目標達成進度 <strong>{rate}%</strong></p></div></section>
            <section className="ranking-card paper-card"><header><div><Trophy /><h2>Preview 門市排行範例</h2></div><span>合成資料</span></header>{revenue.rankings.map((store) => <div className="ranking-row" key={store.store}><strong>{store.rank}</strong><span className="store-mark">瓦</span><span>{store.store}</span><b>{store.revenue.toLocaleString()}</b><small>{store.rate}%</small></div>)}</section>
          </div>
        }}</AsyncGuard>
      </PreviewBoundary>
    </PrimaryShell>
  )
}

const thanksType = {
  warmth: { label: '暖心力', image: gratitudeAssets.warmth },
  wisdom: { label: '智慧力', image: gratitudeAssets.wisdom },
  reliability: { label: '可靠力', image: gratitudeAssets.reliability },
} as const

function ActivityCard({ activity, index = 0 }: { activity: ThanksActivity; index?: number }) {
  const type = thanksType[activity.type]
  return <article className="activity-card paper-card"><img className="activity-avatar" src={index % 2 ? characterAssets.female : characterAssets.male} alt={`${activity.from}的 Q 版人物`} /><div><h3>{activity.from} 感謝 {activity.to}</h3><p>{activity.message}</p><footer><span><img src={type.image} alt="" aria-hidden="true" />{type.label} +1</span><time>{activity.occurredAt}</time></footer></div></article>
}

export function ThanksPage() {
  const data = useAdapterData(adapterLoaders.thanks)
  const previewState = usePreviewState()
  return (
    <PrimaryShell title="感謝">
      <PreviewBoundary state={previewState}>
        <AsyncGuard state={data}>{(activities) => <div className="thanks-page">
          <section className="thanks-score paper-card"><img src={gratitudeAssets.trophy} alt="應援擂台獎盃" /><div><small>應援擂台</small><h2>—</h2><p>Preview 未連正式分數</p></div></section>
          <section className="thanks-types">{Object.values(thanksType).map(({ label, image }) => <article key={label} className="type-card"><img src={image} alt={`${label}圖樣`} /><strong>{label}</strong></article>)}</section>
          <section className="thanks-form paper-card"><h2>傳送感謝</h2><p>Preview 未連分店、同事清單與安全寫入。</p><button className="primary-button" type="button" disabled title="需先完成權限與安全 RPC">發送感謝（未開放）</button></section>
          <section className="latest-feed paper-card"><header><h2>最新感謝動態</h2><Link to="/thanks/activity">查看全部 <ChevronRight /></Link></header>{activities.slice(0, 2).map((activity, index) => <ActivityCard key={activity.id} activity={activity} index={index} />)}</section>
        </div>}</AsyncGuard>
      </PreviewBoundary>
    </PrimaryShell>
  )
}

export function ThanksActivityPage() {
  const data = useAdapterData(adapterLoaders.thanks)
  const previewState = usePreviewState()
  const [type, setType] = useState<'all' | ThanksActivity['type']>('all')
  return <SecondaryShell title="感謝動態" backTo="/thanks"><PreviewBoundary state={previewState}><AsyncGuard state={data}>{(activities) => <div className="thanks-activity-page"><div className="section-switch"><span className="is-active">Preview 感謝範例</span><select aria-label="感謝類型" value={type} onChange={(event) => setType(event.target.value as typeof type)}><option value="all">全部</option>{Object.entries(thanksType).map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}</select></div><section className="activity-list">{activities.filter((activity) => type === 'all' || activity.type === type).map((activity, index) => <ActivityCard key={activity.id} activity={activity} index={index} />)}</section></div>}</AsyncGuard></PreviewBoundary></SecondaryShell>
}

export function BadgesPage() {
  const previewState = usePreviewState()
  return (
    <PrimaryShell>
      <PreviewBoundary state={previewState}>
        <div className="badges-page">
          <section className="gacha-hero paper-card"><div><small>成長獎勵</small><h1>金幣抽獎</h1><p>未完成 ledger-safe RPC 前，Preview 不顯示金幣餘額或假中獎。</p><button className="primary-button" type="button" disabled title="需先完成 ledger-safe RPC">抽獎功能未開放</button></div></section>
          <section><header className="badge-section-title"><h2>勳章素材目錄</h2></header><p>目前展示已提供的徽章素材與分類；個人獲得紀錄待正式系統串接後顯示。</p><div className="badge-grid">{badgeAssets.map((badge) => <Link to={`/badges/${badge.id}`} className="badge-card paper-card" key={badge.id}><img src={badge.src} alt={`${badge.name}勳章`} loading="lazy" decoding="async" /><strong>{badge.name}</strong><small>{badge.category}</small></Link>)}</div></section>
        </div>
      </PreviewBoundary>
    </PrimaryShell>
  )
}

export function BadgeDetailPage() {
  const { badgeId = 'warmth' } = useParams()
  const data = useAdapterData(adapterLoaders.badges)
  return <SecondaryShell title="勳章詳情" backTo="/badges"><AsyncGuard state={data}>{(badges) => {
    const catalogBadge = badgeAssets.find((item) => item.id === badgeId)
    const badge = badges.find((item) => item.id === badgeId)
    const badgeName = catalogBadge?.name ?? badge?.name ?? '勳章'
    const badgeCategory = catalogBadge?.category ?? badge?.category ?? '尚無資料'
    const condition = catalogBadge?.condition ?? '此 Preview 範例未對應正式徽章條件'
    const awardSource = catalogBadge?.awardSource ?? '尚無資料'
    const badgeImage = catalogBadge?.src ?? cultureBadgeAssets[badgeId as keyof typeof cultureBadgeAssets] ?? cultureBadgeAssets.warmth
    return <div className="badge-detail-page"><section className="badge-detail"><img src={badgeImage} alt={`${badgeName}勳章`} /><h1>{badgeName}勳章</h1><span>{badgeCategory}</span></section><section className="badge-progress paper-card"><h2>獲得條件</h2><p>{condition}</p></section><section className="metadata-list paper-card"><div><span>派發方式</span><strong>{awardSource}</strong></div><div><span>勳章類別</span><strong>{badgeCategory}</strong></div></section></div>
  }}</AsyncGuard></SecondaryShell>
}
